import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { fetchRegistrationInit } from '@/api/authApi'
import type { BirthdayFormat, CountryItem, InitCountry } from '@/api/types'
import { registerGoogleMaps } from '@/lib/googleMapsPlugin'
import { setLocaleMessages } from '@/lib/i18n'
import { syncPhoneSelectCountry } from '@/lib/phoneSelectCountry'
import {
  BACK_URL_SESSION_KEY,
  isUsableBackUrl,
  resolveBackUrl,
} from '@/lib/backUrl'
import {
  readSessionJson,
  writeSessionJson,
  writeSessionString,
} from '@/lib/sessionStorage'
import { useRegDataStore } from '@/stores/regDataStore'
import { DEFAULT_SIMPLE_REG_COUNTRIES } from '@/lib/simpleRegistration'

export const useInitStore = defineStore('init', () => {
  const loaded = ref(false)
  const loading = ref(false)
  const backUrl = ref('/')
  const forgotCoralIdPopup = ref(false)
  const birthdayFormats = ref<Record<string, BirthdayFormat>>({})
  const hasInvitation = ref<boolean | null>(null)
  const refererFounded = ref<boolean | null>(null)
  const gotDataYes = ref(false)
  const gotDataNo = ref(false)
  const autoCountry = ref<string[]>([])
  const countryHasAutofit = ref(true)
  const uniqueEmailError = ref('')
  const domainCountry = ref<InitCountry | null>(null)
  const coralIdAfterDubError = ref('')
  const emailAfterDubError = ref('')
  const coralIdAfterRecoveryPage = ref('')
  const emailAfterRecoveryPage = ref('')
  const COUNTRIES_ALL = ref<Record<string, string | CountryItem>>({})
  const COUNTRIES_FILTERED = ref<Record<string, string | CountryItem>>({})
  const languages = ref<Record<string, string>>(
    readSessionJson('languages', {}),
  )
  const languagesInAllCountries = ref<Record<string, Record<string, string>>>(
    {},
  )
  const countryInPhoneSelect = ref(
    readSessionJson<string | null>('countryInPhoneSelect', null),
  )
  const googleObject = ref<Record<string, unknown>>({})
  const errorCityInvitation = ref(false)
  const errorCountriesNotMatch = ref(false)
  const hiddenConsultantBlock = ref(true)
  const visiblePhone = ref(false)
  const latinOnly = ref(false)
  const simpleRegCountries = ref<string[]>([
    ...DEFAULT_SIMPLE_REG_COUNTRIES,
  ])
  const cyrLetters =
    'абвгдеёжзийклмнопрстуфхцчшщъыьэюя' +
    'абвгдеёжзийклмнопрстуфхцчшщъыьэюя'.toUpperCase()
  const latinLetters =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZĀāĂăĄąĆćĈĉĊċČčĎďĐđĒēĔĕĖėĘęĚěĜĝĞğĠġĢģĤĥĦħĨĩĪīĬĭĮįİıĴĵĶķĸĹĺĻļĽľĿŀŁłŃńŅņŇňŉŊŋŌōŎŏŐőŒœŔŕŖŗŘřŚśŜŝŞşŠšŢţŤťŦŧŨũŪūŬŭŮůŰűŲųŴŵŶŷŸÿŹźŻżŽžȘșȚțȜȝȞȟȠƞȢȣȦȧȨȩȪȩȫȬȭȮȯȰȱȲȳȺⱥȾⱦɄʉɅʌɆɇɈɉɊɋɌɍɎɏ'

  const isDomainRu = computed(() => domainCountry.value?.ALPHA2 === 'RU')
  const validLetters = computed(() =>
    latinOnly.value ? latinLetters : latinLetters + cyrLetters,
  )

  function setBackUrl(url: string) {
    backUrl.value = url || '/'
    if (isUsableBackUrl(backUrl.value)) {
      writeSessionString(BACK_URL_SESSION_KEY, backUrl.value)
    }
  }

  function resolveBackUrlFromContext(queryBackUrl?: string | null) {
    setBackUrl(resolveBackUrl(queryBackUrl))
  }

  function setGoogleObject(data: Record<string, unknown>) {
    googleObject.value = { ...data }
  }

  function setLanguages(langs: Record<string, string>) {
    languages.value = { ...langs }
    writeSessionJson('languages', languages.value)
  }

  async function fetchInitialData() {
    loading.value = true
    const regData = useRegDataStore()
    try {
      const init = await fetchRegistrationInit()
      setLocaleMessages(init.lang)

      if (init.COUNTRIES_ALL && Object.keys(init.COUNTRIES_ALL).length) {
        COUNTRIES_ALL.value = { ...init.COUNTRIES_ALL }
      }
      if (init.COUNTRIES_FILTERED && Object.keys(init.COUNTRIES_FILTERED).length) {
        COUNTRIES_FILTERED.value = { ...init.COUNTRIES_FILTERED }
      }
      if (init.langs && Object.keys(init.langs).length) {
        setLanguages(init.langs)
      }
      if (init.langsMore && Object.keys(init.langsMore).length) {
        languagesInAllCountries.value = { ...init.langsMore }
      }
      if (init.birthdayFormats && Object.keys(init.birthdayFormats).length) {
        birthdayFormats.value = { ...init.birthdayFormats }
      }
      if (init.autoCountry?.length) {
        autoCountry.value = [...init.autoCountry]
      }

      if (init.login) {
        regData.setRegScenario('fromLK')
        regData.setRegByReferer(init.login)
        await regData.getConsultantData({ refererLoginValue: init.login })
      }

      if (init.ref) {
        await regData.getConsultantData({ refCodeValue: init.ref })
      }

      if (init.country) {
        regData.setCountry({
          code: init.country.ALPHA2,
          name: init.country.NAME,
          flag: init.country.FLAG || '',
        })
        domainCountry.value = { ...init.country }
        syncPhoneSelectCountry(init.country.ALPHA2, '1')
        syncPhoneSelectCountry(init.country.ALPHA2, '2')
      }

      regData.resolveRegistrationScenario({
        countries: simpleRegCountries.value,
        countryCode: domainCountry.value?.ALPHA2 || init.country?.ALPHA2,
      })

      registerGoogleMaps(init.country?.ALPHA2)
    } catch (error) {
      console.error(error)
      registerGoogleMaps(null)
    } finally {
      loading.value = false
      loaded.value = true
    }
  }

  return {
    loaded,
    loading,
    backUrl,
    forgotCoralIdPopup,
    birthdayFormats,
    hasInvitation,
    refererFounded,
    gotDataYes,
    gotDataNo,
    autoCountry,
    countryHasAutofit,
    uniqueEmailError,
    domainCountry,
    coralIdAfterDubError,
    emailAfterDubError,
    coralIdAfterRecoveryPage,
    emailAfterRecoveryPage,
    COUNTRIES_ALL,
    COUNTRIES_FILTERED,
    languages,
    languagesInAllCountries,
    countryInPhoneSelect,
    googleObject,
    errorCityInvitation,
    errorCountriesNotMatch,
    hiddenConsultantBlock,
    visiblePhone,
    latinOnly,
    isDomainRu,
    validLetters,
    setBackUrl,
    resolveBackUrlFromContext,
    setGoogleObject,
    setLanguages,
    fetchInitialData,
  }
})
